import React, { useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useRemoteGet } from './useRemoteGet';
import PagingContext, { PagingOptions } from '@/contexts/PagingContext';

// Hook per utilizzare il contesto con un ID specifico
export const usePaging = (contextId: string = 'default') => {
	const context = useContext(PagingContext);
	if (context === undefined) {
		throw new Error('usePaging must be used within a PagingProvider');
	}

	const contextItem = context.getContextItem(contextId);

	return {
		pagingOptions: contextItem.pagingOptions,
		totalPages: contextItem.totalPages,
		totalElements: contextItem.totalElements,
		setPagingOptions: (options: Partial<PagingOptions>) => context.setPagingOptions(contextId, options),
		setTotalInfo: (totalPages: number, totalElements: number) =>
			context.setTotalInfo(contextId, totalPages, totalElements),
		resetPaging: () => context.resetPaging(contextId)
	};
};

/*
Hook per effettuare chiamate API paginate
- Accetta parametri: metodo API, opzioni iniziali, ID contesto, se è lazy
- Utilizza l'hook usePaging per accedere al contesto
- Tiene traccia degli stati precedenti per evitare loop infiniti
- Restituisce: stato di caricamento, errori, funzioni di controllo
*/
export const usePaginatedApi = <T extends object>(
	apiMethod: (pagingOptions: PagingOptions) => Promise<{
		content?: T[];
		totalElements?: number;
		totalPages?: number;
		number?: number;
		size?: number;
		pageable?: {
			pageNumber?: number;
			pageSize?: number;
		};
	}>,
	initialOptions?: Partial<PagingOptions>,
	contextId: string = 'default',
	lazy: boolean = false
) => {
	const { pagingOptions, setPagingOptions, setTotalInfo } = usePaging(contextId);
	const [initialOptionsApplied, setInitialOptionsApplied] = useState(false);
	const isFirstRender = useRef(true);
	const previousPagingOptions = useRef(pagingOptions);
	const shouldRefetch = useRef(false);

	// Applica le opzioni iniziali se fornite, solo al primo rendering
	useEffect(() => {
		if (initialOptions && !initialOptionsApplied && isFirstRender.current) {
			setPagingOptions(initialOptions);
			setInitialOptionsApplied(true);
			isFirstRender.current = false;
		}
	}, [initialOptions, setPagingOptions, initialOptionsApplied]);

	// Controlla se le opzioni di paginazione sono cambiate significativamente per giustificare un refetch
	useEffect(() => {
		// Confronta solo le proprietà rilevanti per evitare refetch non necessari
		const hasChanged =
			previousPagingOptions.current.page !== pagingOptions.page ||
			previousPagingOptions.current.size !== pagingOptions.size ||
			previousPagingOptions.current.sortBy !== pagingOptions.sortBy ||
			previousPagingOptions.current.sortDir !== pagingOptions.sortDir;

		if (hasChanged) {
			previousPagingOptions.current = { ...pagingOptions };
			shouldRefetch.current = true;
		}
	}, [pagingOptions]);

	// Prepara il metodo remoto per l'hook useRemoteGet
	const remoteMethod = useCallback(async () => {
		const response = await apiMethod(pagingOptions);

		// Aggiorna le informazioni sulla paginazione dal response, gestendo i valori undefined
		setTotalInfo(response.totalPages || 0, response.totalElements || 0);

		return response;
	}, [apiMethod, pagingOptions, setTotalInfo]);

	// Usa l'hook esistente per la chiamata API
	const remoteGetResult = useRemoteGet<{
		content?: T[];
		totalElements?: number;
		totalPages?: number;
		number?: number;
		size?: number;
		pageable?: {
			pageNumber?: number;
			pageSize?: number;
		};
	}>({
		remoteMethod,
		lazy: true // lazy per controllare meglio quando fare fetch
	});

	// Effettua il fetch solo quando necessario
	useEffect(() => {
		// Prima richiesta (se non lazy) o quando dovrebbe fare refetch
		if ((!lazy && isFirstRender.current) || shouldRefetch.current) {
			remoteGetResult.fetch();
			shouldRefetch.current = false;
		}
	}, [lazy, remoteGetResult.fetch, pagingOptions]);

	return {
		data: remoteGetResult.data?.content || [],
		totalElements: remoteGetResult.data?.totalElements || 0,
		totalPages: remoteGetResult.data?.totalPages || 0,
		currentPage: remoteGetResult.data?.number || remoteGetResult.data?.pageable?.pageNumber || 0,
		loading: remoteGetResult.loading,
		error: remoteGetResult.error,
		fetch: remoteGetResult.fetch,
		pagingOptions,
		setPagingOptions
	};
};
